import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { arretsMaladieAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Plus, Activity, CheckCircle2, Clock, XCircle, FileText,
  Trash2, Eye, ChevronRight, AlertTriangle, Download,
} from 'lucide-react';
import { exportArretMaladiePDF } from '../utils/exportPDF';

const STATUT_CFG = {
  SOUMIS: { label: 'En attente',  color: '#0891B2', bg: 'rgba(8,145,178,0.10)',  icon: Clock },
  VALIDE: { label: 'Validé',      color: '#059669', bg: 'rgba(5,150,105,0.10)',  icon: CheckCircle2 },
  REJETE: { label: 'Rejeté',      color: '#DC2626', bg: 'rgba(220,38,38,0.10)', icon: XCircle },
};

function StatutBadge({ statut }) {
  const cfg = STATUT_CFG[statut] || STATUT_CFG.SOUMIS;
  const Ic = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 9px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700,
      background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap',
    }}>
      <Ic size={10} />{cfg.label}
    </span>
  );
}

export default function ArretsMaladie() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin  = ['super_admin', 'admin', 'rh'].includes(user?.role?.toLowerCase());

  const [arrets,  setArrets]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    arretsMaladieAPI.list({ user_id: user?.id })
      .then(r => setArrets(r.data || []))
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet arrêt maladie ?')) return;
    try {
      await arretsMaladieAPI.delete(id);
      setArrets(a => a.filter(x => x.id !== id));
      toast.success('Arrêt supprimé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
  };

  const counts = { SOUMIS: 0, VALIDE: 0, REJETE: 0 };
  arrets.forEach(a => { if (counts[a.statut] !== undefined) counts[a.statut]++; });

  return (
    <div style={{ padding: '28px 40px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(220,38,38,0.12)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Activity size={18} color="#DC2626" />
            </div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>
              Arrêts maladie
            </h1>
          </div>
          <p style={{ margin: '6px 0 0 46px', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Déposez et suivez vos arrêts de travail. Un justificatif médical est obligatoire.
          </p>
        </div>
        <button
          onClick={() => navigate('/arrets-maladie/nouveau')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 10, border: 'none',
            background: '#DC2626', color: '#fff', cursor: 'pointer',
            fontWeight: 700, fontSize: '0.875rem', flexShrink: 0,
          }}>
          <Plus size={16} /> Nouvel arrêt
        </button>
      </div>

      {/* Compteurs */}
      {!loading && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          {Object.entries(STATUT_CFG).map(([key, cfg]) => {
            const Ic = cfg.icon;
            return (
              <div key={key} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 18px', borderRadius: 12,
                border: '1px solid var(--border)', background: 'var(--bg-card)',
              }}>
                <Ic size={16} color={cfg.color} />
                <div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>
                    {counts[key]}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: cfg.color, fontWeight: 600 }}>{cfg.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Chargement…</div>
      ) : arrets.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14,
        }}>
          <Activity size={36} style={{ color: 'var(--text-muted)', opacity: 0.3, margin: '0 auto 12px' }} />
          <div style={{ fontWeight: 600, color: 'var(--text-muted)', marginBottom: 12 }}>
            Aucun arrêt maladie déposé
          </div>
          <button onClick={() => navigate('/arrets-maladie/nouveau')}
            style={{ padding: '8px 18px', borderRadius: 9, border: 'none',
                     background: 'rgba(220,38,38,0.12)', color: '#DC2626',
                     cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}>
            <Plus size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
            Déposer un arrêt maladie
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {arrets.map(a => {
            const statut = a.statut || 'SOUMIS';
            const cfg = STATUT_CFG[statut] || STATUT_CFG.SOUMIS;
            const canDelete = statut !== 'VALIDE' || isAdmin;
            return (
              <div key={a.id} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 13, padding: '16px 20px',
                display: 'flex', alignItems: 'center', gap: 16,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 11, flexShrink: 0,
                  background: `${cfg.color}12`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Activity size={20} color={cfg.color} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
                      Du {a.date_debut} au {a.date_fin}
                    </span>
                    <StatutBadge statut={statut} />
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {a.nb_jours}j
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    {a.depart_midi  && <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Départ midi</span>}
                    {a.retour_midi  && <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Retour 14h</span>}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      <FileText size={11} /> {a.nb_justificatifs ?? a.justificatifs?.length ?? 0} justificatif(s)
                    </span>
                    {a.description && (
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        {a.description}
                      </span>
                    )}
                  </div>
                  {statut === 'REJETE' && a.commentaire_rh && (
                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 6,
                                  padding: '6px 10px', borderRadius: 7,
                                  background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.18)' }}>
                      <AlertTriangle size={12} style={{ color: '#DC2626', flexShrink: 0, marginTop: 1 }} />
                      <span style={{ fontSize: '0.75rem', color: '#DC2626' }}>Motif : {a.commentaire_rh}</span>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => navigate(`/arrets-maladie/${a.id}`)}
                    title="Consulter"
                    style={{
                      width: 34, height: 34, borderRadius: 8, cursor: 'pointer',
                      border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'var(--text-secondary)',
                    }}>
                    <Eye size={15} />
                  </button>
                  {['VALIDE', 'REJETE'].includes(statut) && (
                    <button
                      onClick={() => exportArretMaladiePDF(a)}
                      title="Télécharger PDF"
                      style={{
                        width: 34, height: 34, borderRadius: 8, cursor: 'pointer',
                        border: '1px solid rgba(5,150,105,0.25)', background: 'rgba(5,150,105,0.06)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#059669',
                      }}>
                      <Download size={15} />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(a.id)}
                      title="Supprimer"
                      style={{
                        width: 34, height: 34, borderRadius: 8, cursor: 'pointer',
                        border: '1px solid rgba(220,38,38,0.25)', background: 'rgba(220,38,38,0.06)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#DC2626',
                      }}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
